import { css, styled, themeType } from '../../hooks/theme';
import { getCssFont } from '../../styles';
import { EVariant, Size, Tokens, Variant } from '../../types';

export interface ContainerProps {
  size?: Size;
  variant?: Variant;
  error?: boolean;
  typeButton?: 'button' | 'iconButton';
}

const BaseButton = styled.button<ContainerProps>`
  ${props => {
    const theme = props.theme;
    const button = Tokens.buttons[props.typeButton || 'button'];
    return css`
      ${getCssFont(theme.font.desktop.paragraph.p2.semibold)}
      height: ${button.size[props.size] || button.size.lg};
      ${props.typeButton === 'iconButton' &&
      css`
        width: ${button.size[props.size] || button.size.lg};
      `}
      border-radius: ${props.theme.borderRadius.sm}px;
      padding: ${button.padding};

      /* &:focus {
        outline: 0;
        box-shadow: ${Tokens.boxShadow};
      } */
    `;
  }}

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  cursor: pointer;

  transition: background 0.2s, color 0.2s, border 0.2s;
`;

BaseButton.defaultProps = {
  theme: Tokens.eixods,
};

export const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

interface ButtonStyleProps extends ContainerProps {
  theme: themeType;
}

const containedButton = ({ theme, ...props }: ButtonStyleProps) => css`
  background: ${theme.colors.brand.primary[100]};
  border: 1px solid ${theme.colors.brand.primary[100]};
  color: ${theme.colors.neutral.grey[0]};

  &:hover {
    background: ${theme.colors.brand.primary[80]};
    border: 1px solid ${theme.colors.brand.primary[80]};
  }
  &:active {
    background: ${theme.colors.brand.primary[120]};
    border: 1px solid ${theme.colors.brand.primary[120]};
  }

  ${props.error &&
  css`
    background: ${theme.colors.feedback.error};
    border: 1px solid ${theme.colors.feedback.error};
    &:hover {
      background: ${theme.colors.support.vanderleia[80]};
      border: 1px solid ${theme.colors.support.vanderleia[80]};
    }
    &:active {
      background: ${theme.colors.support.vanderleia[120]};
      border: 1px solid ${theme.colors.support.vanderleia[120]};
    }
  `}

  &:disabled {
    background: ${theme.colors.neutral.grey[10]};
    border: 1px solid ${theme.colors.neutral.grey[10]};
    color: ${theme.colors.neutral.grey[70]};
    cursor: not-allowed;
  }
`;

const outlinedButton = ({ theme, ...props }: ButtonStyleProps) => css`
  background: ${theme.colors.neutral.grey[0]};
  color: ${theme.colors.brand.primary[100]};
  border: 1px solid ${theme.colors.brand.primary[100]};

  &:hover {
    background: ${theme.colors.brand.primary[1]};
  }

  &:active {
    background: ${theme.colors.brand.primary[120]};
    color: ${theme.colors.neutral.grey[0]};
    border: 1px solid ${theme.colors.brand.primary[120]};
  }

  ${props.error &&
  css`
    border: 1px solid ${theme.colors.feedback.error};
    color: ${theme.colors.feedback.error};
    &:hover {
      background: ${theme.colors.support.vanderleia[80]};
      color: ${theme.colors.neutral.grey[0]};
    }
    &:active {
      background: ${theme.colors.support.vanderleia[120]};
      border: 1px solid ${theme.colors.support.vanderleia[120]};
    }
  `}

  &:disabled {
    background: ${theme.colors.neutral.grey[0]};
    color: ${theme.colors.neutral.grey[10]};
    border: 1px solid ${theme.colors.neutral.grey[10]};
    cursor: not-allowed;
  }
`;

const textButton = ({ theme, ...props }: ButtonStyleProps) => css`
  gap: 0;
  padding: 0;

  background: ${theme.colors.neutral.grey[0]};
  border: none;
  color: ${theme.colors.brand.primary[100]};

  &:hover {
    color: ${theme.colors.brand.primary[80]};
  }

  &:active {
    color: ${theme.colors.brand.primary[120]};
  }

  ${props.error &&
  css`
    color: ${theme.colors.feedback.error};
    &:hover {
      color: ${theme.colors.feedback.error};
    }
    &:active {
      color: ${theme.colors.support.vanderleia[120]};
    }
  `}

  &:disabled {
    border: 1px solid ${theme.colors.neutral.grey[0]};
    color: ${theme.colors.neutral.grey[10]};
    cursor: not-allowed;
  }
`;

export const Container = styled(BaseButton)<ContainerProps>`
  ${props => {
    switch (props.variant) {
      case EVariant.outlined:
        return outlinedButton(props);
      case EVariant.text:
        return textButton(props);
      default:
        return containedButton(props);
    }
  }}
`;
